// src/constants/defaultVoices.ts
/**
 * Default ElevenLabs Voices
 *
 * Pre-made voices available from ElevenLabs that users can use
 * without needing to clone their own voice.
 *
 * Voice IDs are from ElevenLabs' public voice library.
 * https://elevenlabs.io/docs/voices/premade-voices
 */

import type { IDefaultVoice } from '@/@types/voices';

/**
 * ElevenLabs pre-made voices with their IDs and metadata
 */
export const DEFAULT_VOICES: IDefaultVoice[] = [
  // Female voices
  {
    id: 'EXAVITQu4vr4xnSDxMaL',
    name: 'Sarah',
    description: 'Soft and friendly female voice, great for narration',
    gender: 'female',
    accent: 'American',
    useCase: 'narration',
    previewUrl:
      'https://storage.googleapis.com/eleven-public-prod/premade/voices/EXAVITQu4vr4xnSDxMaL/01a3e33c-6e99-4ee7-8543-ff2216a32186.mp3',
  },
  {
    id: '21m00Tcm4TlvDq8ikWAM',
    name: 'Rachel',
    description: 'Calm and composed female voice, ideal for audiobooks',
    gender: 'female',
    accent: 'American',
    useCase: 'audiobook',
    previewUrl:
      'https://storage.googleapis.com/eleven-public-prod/premade/voices/21m00Tcm4TlvDq8ikWAM/b4928a68-c03b-411f-8533-3d5c299fd451.mp3',
  },
  {
    id: 'XrExE9yKIg1WjnnlVkGX',
    name: 'Matilda',
    description: 'Warm and engaging female voice, perfect for educational content',
    gender: 'female',
    accent: 'American',
    useCase: 'education',
    previewUrl:
      'https://storage.googleapis.com/eleven-public-prod/premade/voices/XrExE9yKIg1WjnnlVkGX/b930e18d-6b4d-466e-bab2-0ae97c6d8535.mp3',
  },
  {
    id: 'pFZP5JQG7iQjIQuC4Bku',
    name: 'Lily',
    description: 'Youthful and energetic female voice, great for social media',
    gender: 'female',
    accent: 'British',
    useCase: 'social_media',
    previewUrl:
      'https://storage.googleapis.com/eleven-public-prod/premade/voices/pFZP5JQG7iQjIQuC4Bku/89b68b35-b3dd-4348-a84a-a3c13a3c2b30.mp3',
  },
  // Male voices
  {
    id: 'pNInz6obpgDQGcFmaJgB',
    name: 'Adam',
    description: 'Deep and authoritative male voice, perfect for narration',
    gender: 'male',
    accent: 'American',
    useCase: 'narration',
    previewUrl:
      'https://storage.googleapis.com/eleven-public-prod/premade/voices/pNInz6obpgDQGcFmaJgB/d6905d7a-dd26-4187-bfff-1bd3a5ea7cac.mp3',
  },
  {
    id: 'ErXwobaYiN019PkySvjV',
    name: 'Antoni',
    description: 'Smooth and professional male voice, ideal for business content',
    gender: 'male',
    accent: 'American',
    useCase: 'business',
    previewUrl:
      'https://storage.googleapis.com/eleven-public-prod/premade/voices/ErXwobaYiN019PkySvjV/2d5ab2a3-4578-470f-b797-6331e46a7d55.mp3',
  },
  {
    id: 'VR6AewLTigWG4xSOukaG',
    name: 'Arnold',
    description: 'Strong and confident male voice, great for dramatic content',
    gender: 'male',
    accent: 'American',
    useCase: 'dramatic',
    previewUrl:
      'https://storage.googleapis.com/eleven-public-prod/premade/voices/VR6AewLTigWG4xSOukaG/49a22885-80d5-48e8-87a3-076fc9193d9a.mp3',
  },
  {
    id: 'yoZ06aMxZJJ28mfd3POQ',
    name: 'Sam',
    description: 'Friendly and conversational male voice, perfect for podcasts',
    gender: 'male',
    accent: 'American',
    useCase: 'podcast',
    previewUrl:
      'https://storage.googleapis.com/eleven-public-prod/premade/voices/yoZ06aMxZJJ28mfd3POQ/b017ad02-8d18-4456-ad92-55c85ecf6363.mp3',
  },
  {
    id: 'TxGEqnHWrfWFTfGW9XjX',
    name: 'Josh',
    description: 'Young and dynamic male voice, ideal for gaming content',
    gender: 'male',
    accent: 'American',
    useCase: 'gaming',
    previewUrl:
      'https://storage.googleapis.com/eleven-public-prod/premade/voices/TxGEqnHWrfWFTfGW9XjX/47de9a7e-773a-42a8-b410-4aa90c581216.mp3',
  },
  {
    id: 'onwK4e9ZLuTAKqWW03F9',
    name: 'Daniel',
    description: 'British male voice with clear diction, great for documentaries',
    gender: 'male',
    accent: 'British',
    useCase: 'documentary',
    previewUrl:
      'https://storage.googleapis.com/eleven-public-prod/premade/voices/onwK4e9ZLuTAKqWW03F9/7eee0236-1a72-4b86-b303-5dcadc007ba9.mp3',
  },
] as const;

/**
 * Get default voice by ID
 */
export function getDefaultVoiceById(voiceId: string): IDefaultVoice | undefined {
  return DEFAULT_VOICES.find(voice => voice.id === voiceId);
}

/**
 * Check if a voice ID is a default voice
 */
export function isDefaultVoice(voiceId: string): boolean {
  return DEFAULT_VOICES.some(voice => voice.id === voiceId);
}
